import LuminisHttpClient from '../helpers/luminis-http-client.es6.js';
import ApiUtils from "../api-utils.es6.js";
import { SESSION_STORAGE_KEYS } from "@js/constants/js-constants.es6.js";
import SessionStorageUtils from "@js/utils/session-storage-utils.es6.js";

export default class LuminisServiceUtils {
  #luminisHttpClient;

  constructor(luminisHttpClient = null) {
    if (!luminisHttpClient) {
      luminisHttpClient = new LuminisHttpClient();
    }
    this.#luminisHttpClient = luminisHttpClient;
  }

  async getPatientEmail(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'Patient/Email',
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient email", errorRedirectPage, successRedirectPage);
  }

  async getProviderOnlineSchedulingAvailabilityInfo(npi, exclusionsList) {
    const options = {
      relativePath: `Provider/${npi}/AvailabilityInfo`,
    };

    const availabilityInfo = await this.#luminisHttpClient.get(options);
    return LuminisServiceUtils.filterExclusionsFromAvailability(
      exclusionsList,
      availabilityInfo,
    );
  }

  async getProviderTeam(npi, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Provider/${npi}/Team`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving Provider's Team", errorRedirectPage, successRedirectPage);
  }

  static filterExclusionsFromAvailability(exclusionsList, availabilityInfo) {
    const originalList = availabilityInfo.visitReasonMnemonicAvailability;
    const filteredList = originalList.filter(
      mnemonic => !exclusionsList.includes(mnemonic),
    );
    availabilityInfo.hasAvailability = filteredList.length > 0;
    availabilityInfo.visitReasonMnemonicAvailability = filteredList;
    return availabilityInfo;
  }

  static async getExclusionMnemonics() {
    const jsonapiQuery = `/${
      window.location.pathname.split('/')[1]
    }/jsonapi/taxonomy_term/mnemonic_exclusions`;
    const response = await ApiUtils.fetchJSONApiRequest(
      jsonapiQuery,
    );

    if (response.data) {
      return response.data.map(item => item.attributes.name);
    }
    return [];
  }

  async scheduleOpenSchedulingAppointmentAuthenticated({
    reservationKey,
    visitReasonMnemonic,
    epicVisitTypeId,
    epicDepartmentId,
    epicProviderId,
    slot,
    visitDetails,
    insurance,
    isSelfPay,
    referralImage,
    patientInstructions,
    language,
  }, errorRedirectPage = null, successRedirectPage = null) {
    const notes = [];
    if (visitDetails) {
      notes.push(`Additional Visit Details: ${visitDetails}`);
    }

    const body = {
      reservationKey,
      visitReasonMnemonic,
      epicVisitTypeId,
      epicDepartmentId,
      epicProviderId,
      slot,
      notes,
      insurance: insurance.toLuminisApiObject(),
      isSelfPay,
      referralImage,
      patientInstructions,
      language,
    };

    const options = {
      relativePath: 'Appointment/Book/Open/Authenticated',
      body,
    };

    return this.#luminisHttpClient.postJson(options, "Error booking Authenticated Appointment", errorRedirectPage, successRedirectPage);
  }

  async scheduleOpenSchedulingAppointmentUnauthenticated({
    reservationKey,
    visitReasonMnemonic,
    epicVisitTypeId,
    epicDepartmentId,
    epicProviderId,
    slot,
    patientDemographics,
    preferredContactMethod,
    visitDetails,
    insurance,
    isSelfPay,
    referralImage,
    patientInstructions,
    language,
  }, errorRedirectPage = null, successRedirectPage = null) {
    const notes = [];
    if (patientDemographics?.genderInformation) {
      notes.push(
        `Additional Gender Information: ${patientDemographics.genderInformation}`,
      );
    }
    if (preferredContactMethod) {
      notes.push(`Preferred Contact Method: ${preferredContactMethod}`);
    }
    if (visitDetails) {
      notes.push(`Additional Visit Details: ${visitDetails}`);
    }

    const body = {
      reservationKey,
      visitReasonMnemonic,
      epicVisitTypeId,
      epicDepartmentId,
      epicProviderId,
      slot,
      patient: patientDemographics.toLuminisApiObject(),
      notes,
      insurance: insurance.toLuminisApiObject(),
      isSelfPay,
      referralImage,
      patientInstructions,
      language,
    };

    const options = {
      relativePath: 'Appointment/Book/Open/Unauthenticated',
      body,
    };

    return this.#luminisHttpClient.postJson(options, "Error booking unauthenticated appointment", errorRedirectPage, successRedirectPage);
  }

  async scheduleDirectSchedulingAppointment({
    reservationKey,
    visitReasonMnemonic,
    epicVisitTypeId,
    epicDepartmentId,
    epicProviderId,
    slot,
    durationMins,
    visitDetails,
    insurance,
    isSelfPay,
    referralImage,
    patientInstructions,
    language,
  }, errorRedirectPage = null, successRedirectPage = null) {
    const notes = [];
    if (visitDetails) {
      notes.push(`Additional Visit Details: ${visitDetails}`);
    }

    const body = {
      reservationKey,
      visitReasonMnemonic,
      epicVisitTypeId,
      epicDepartmentId,
      epicProviderId,
      slot,
      durationMins,
      notes,
      insurance: insurance.toLuminisApiObject(),
      isSelfPay,
      referralImage,
      patientInstructions,
      language,
    };

    const options = {
      relativePath: 'Appointment/Book/Direct',
      body,
    };

    return this.#luminisHttpClient.postJson(options, "Error booking Direct Appointment", errorRedirectPage, successRedirectPage);
  }

  async getVirtualUrgentCareHoursOfOperation(daysAhead = 6, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `VirtualUrgentCare/HoursOfOperation?daysAhead=${daysAhead}`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving VUC Hours of Operation", errorRedirectPage, successRedirectPage);
  }

  async getVirtualUrgentCareAcceptingPatients(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/AcceptingPatients',
    };

    return this.#luminisHttpClient.get(options, "Error retrieving VUC Accepting Patients", errorRedirectPage, successRedirectPage);
  }

  async getVirtualUrgentCareCoverages(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/Coverages',
    };

    return this.#luminisHttpClient.get(options, "Error retrieving VUC coverages", errorRedirectPage, successRedirectPage);
  }

  async scheduleVirtualUrgentCareOpeningNotification(phoneNumber, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/ScheduleOpeningNotification',
      body: {
        phoneNumber,
      },
    };

    await this.#luminisHttpClient.postJson(options, "Error sending VUC Schedule open notification", errorRedirectPage, successRedirectPage);
  }

  async getVirtualUrgentCarePatientCount(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/PatientCount',
    };

    return this.#luminisHttpClient.get(options, "Error Retrieving VUC patient count", errorRedirectPage, successRedirectPage);
  }

  async getVirtualUrgentCareEstimatedWaitTime(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/EstimatedWaitTime',
    };

    return this.#luminisHttpClient.get(options, "Error retrieving VUC estimated wait time", errorRedirectPage, successRedirectPage);
  }

  async beginVirtualUrgentCareSessionAuthenticated({
    visitDetails,
    interpreterNeeded,
    insurance,
    isSelfPay,
  }, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/Session/Authenticated',
      body: {
        reasonForVisit: visitDetails,
        interpreterNeeded,
        insurance: insurance.toLuminisApiObject(),
        isSelfPay,
      },
    };

    return this.#luminisHttpClient.postJson(options, "Error beginning authenticated VUC Session", errorRedirectPage, successRedirectPage);
  }

  async beginVirtualUrgentCareSessionUnauthenticated({
    patientDemographics,
    visitDetails,
    interpreterNeeded,
    insurance,
    isSelfPay,
  }, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'VirtualUrgentCare/Session/Unauthenticated',
      body: {
        patient: patientDemographics.toLuminisApiObject(),
        reasonForVisit: visitDetails,
        interpreterNeeded,
        insurance: insurance.toLuminisApiObject(),
        isSelfPay,
      },
    };

    return this.#luminisHttpClient.postJson(options, "Error beginning unauthenticated VUC Session", errorRedirectPage, successRedirectPage);
  }

  async getVirtualUrgentCareSession(id, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `VirtualUrgentCare/Session/${id}`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving VUC Session id", errorRedirectPage, successRedirectPage);
  }

  async endVirtualUrgentCareSession(id, errorRedirectPage, successRedirectPage = null) {
    const options = {
      relativePath: `VirtualUrgentCare/Session/${id}`,
    };

    await this.#luminisHttpClient.delete(options, "Error deleting VUC Session", errorRedirectPage, successRedirectPage);
  }

  async cancelAppointment(csn, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'Appointment/Cancel',
      body: {
        appointmentCsn: csn,
        comment: 'Cancelled via appointment dashboard',
      },
    };

    return this.#luminisHttpClient.putJson(options, "Error canceling appointment", errorRedirectPage, successRedirectPage);
  }

  async checkBookAgain(mnemonic, errorRedirectPage = null, successRedirectPage = null) {
    // default to referral true so we show max results.
    const options = {
      relativePath: `Schedule/AvailabilityInfo?VisitReasonMnemonic=${mnemonic}&PatientHasReferral=true`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving availability information", errorRedirectPage, successRedirectPage);
  }

  async getAvailabilitiesOptimized(condition, startDate, endDate, hasReferral, errorRedirectPage = null, successRedirectPage = null) {
    const endpointWithParams = `Schedule/VisitReason/Availabilities_Optimized?VisitReasonMnemonic=${condition}&StartDate=${startDate}&EndDate=${endDate}&PatientHasReferral=${hasReferral}`;

    const options = {
      relativePath: endpointWithParams,
    };

    const response = await this.#luminisHttpClient.get(options, "Error retrieving optimized availabilities", errorRedirectPage, successRedirectPage);

    const unavailableSlot = LuminisServiceUtils.getAndRemoveUnavailableSlotFromSessionStorage();
    if (!unavailableSlot) {
      return response;
    }

    const filteredResponse = LuminisServiceUtils.filterUnavailableSlotFromVisitReasonAvailabilitiesResponse(
      response,
      unavailableSlot.providerId,
      unavailableSlot.departmentId,
      unavailableSlot.time);

    return filteredResponse;
  }

  static getAndRemoveUnavailableSlotFromSessionStorage() {
    // If the patient is coming from a slot reservation failure, the API may not have
    // been able to remove the slot from the results yet. We store the unavailable slot
    // information in session storage so that we can manually hide the slot.
    const unavailableSlotIdentifier = SessionStorageUtils.getItem(SESSION_STORAGE_KEYS.UNAVAILABLE_SLOT);
    if (!unavailableSlotIdentifier) {
      return null;
    }

    // The API should have time to catch up and remove the unavailable slot on future
    // fetches. Clear the unavailable slot information from session storage.
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.UNAVAILABLE_SLOT);

    // unavailableSlotIdentifier format: epicProviderId|epicDepartmentId|slotTime
    const [providerId, departmentId, time] = unavailableSlotIdentifier.split('|');

    return { providerId, departmentId, time };
  }

  static filterUnavailableSlotFromVisitReasonAvailabilitiesResponse(response, unavailableProviderId, unavailableDepartmentId, unavailableTime) {
    response.forEach(providerAndDepartment => {
      const isUnavailableProvider = providerAndDepartment.provider.id === unavailableProviderId;
      if (!isUnavailableProvider) {
        return;
      }

      LuminisServiceUtils.filterUnavailableSlotFromDepartmentAvailabilities(
        providerAndDepartment.departmentAvailabilities,
        unavailableDepartmentId,
        unavailableTime);
    });

    return response;
  }

  static filterUnavailableSlotFromDepartmentAvailabilities(departmentAvailabilitiesArray, unavailableDepartmentId, unavailableTime) {
    departmentAvailabilitiesArray.forEach(departmentAvailability => {
      const isUnavailableDepartment = departmentAvailability.department.id === unavailableDepartmentId;
      if (!isUnavailableDepartment) {
        return;
      }

      // If we are here, we already know that the provider and department match the unavailable slot. Now, filter out the exact time.
      const availabilities = Object.values(departmentAvailability.availabilities).filter(availability => availability.slot !== unavailableTime);

      departmentAvailability.availabilities = availabilities;
    });
  }

  async getMychartProviderData(condition, errorRedirectPage = null, successRedirectPage = null) {
    const endpointWithParams = `Schedule/VisitReason/EligibleDates?visitReasonMnemonic=${condition}`;
    const options = {
      relativePath: endpointWithParams,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving provider eligible dates for a given mnemonic", errorRedirectPage, successRedirectPage);
  }

  async fetchLoggedInCareTeam(errorRedirectPage = null, successRedirectPage = null) {
    const endpointWithParams = 'Patient/CareTeam';
    const options = {
      relativePath: endpointWithParams,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient care team", errorRedirectPage, successRedirectPage);
  }

  async unreserveTimeSlot(reservationKey, errorRedirectPage = null, successRedirectPage = null) {
     const dataToSend = {
      "reservationKey":reservationKey
    }

    const options = {
      relativePath: 'Appointment/Release',
      body: dataToSend
    };

    return this.#luminisHttpClient.putJson(options, "Error unreserving time slot", errorRedirectPage, successRedirectPage);
  }

  async reserveOpenSchedulingTimeSlot(epicVisitTypeId, deptIdSelected, epicProviderId, timeSlot, durationMins, minsToReserve, errorRedirectPage = null, successRedirectPage = null) {
    const dataToSend = {
      "epicVisitTypeId": epicVisitTypeId,
      "epicDepartmentId": deptIdSelected,
      "epicProviderId": epicProviderId,
      "slot": timeSlot,
      "durationMins": durationMins,
      "minsToReserve": minsToReserve
    }

    const options = {
      relativePath: 'Appointment/Reserve/Open',
      body: dataToSend
    };

    return this.#luminisHttpClient.putJson(options, "Error reserving open scheduling time slot", errorRedirectPage, successRedirectPage);
  }

  async reserveDirectSchedulingTimeSlot(epicVisitTypeId, deptIdSelected, epicProviderId, timeSlot, durationMins, minsToReserve, errorRedirectPage = null, successRedirectPage = null) {
    const dataToSend = {
      "epicVisitTypeId": epicVisitTypeId,
      "epicDepartmentId": deptIdSelected,
      "epicProviderId": epicProviderId,
      "slot": timeSlot,
      "durationMins": durationMins,
      "minsToReserve": minsToReserve
    }

    const options = {
      relativePath: 'Appointment/Reserve/Direct',
      body: dataToSend
    };

    return this.#luminisHttpClient.putJson(options, "Error reserving direct scheduling time slot", errorRedirectPage, successRedirectPage);
  }

  async getProviderAvailabilities(npi, startDate, endDate, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Provider/${npi}/Availabilities?StartDate=${startDate}&EndDate=${endDate}`,
    };

    const response = await this.#luminisHttpClient.get(options, "Error retrieving provider availabilities", errorRedirectPage, successRedirectPage);

    const providerId = await this.getProviderEpicId(npi);

    const unavailableSlot = LuminisServiceUtils.getAndRemoveUnavailableSlotFromSessionStorage();
    if (!unavailableSlot) {
      return response;
    }

    const isUnavailableProvider = providerId === unavailableSlot.providerId;
    if (!isUnavailableProvider) {
      return;
    }

    const filteredResponse = await LuminisServiceUtils.filterUnavailableSlotFromProviderAvailabilitiesResponse(
      response,
      unavailableSlot.departmentId,
      unavailableSlot.time);

    return filteredResponse;
  }

  static filterUnavailableSlotFromProviderAvailabilitiesResponse(response, unavailableDepartmentId, unavailableTime) {
    response.forEach(visitReasonAndDepartment => {
      LuminisServiceUtils.filterUnavailableSlotFromDepartmentAvailabilities(
        visitReasonAndDepartment.departmentAvailabilities,
        unavailableDepartmentId,
        unavailableTime);
    });

    return response;
  }

  async getProviderEligibleDates(npi, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Provider/${npi}/EligibleDates`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving provider eligible dates", errorRedirectPage, successRedirectPage);
  }

  async getProviderEpicId(npi, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Provider/${npi}/EpicProviderId`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving provider epic id", errorRedirectPage, successRedirectPage);
  }

  async getPatientMedications(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Patient/Medications`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient medications", errorRedirectPage, successRedirectPage);
  }

  async getPatientPastAppointments(positionIndex = null, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'Patient/PastAppointments',
    };

    if (positionIndex) {
      options.relativePath += `?positionIndex=${positionIndex}`;
    }

    return this.#luminisHttpClient.get(options, "Error retrieving patient past appointments", errorRedirectPage, successRedirectPage);
  }

  async getPatientAllPastAppointments(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'Patient/PastAppointments/All',
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient all past appointments", errorRedirectPage, successRedirectPage);
  }

  async getPatientFutureAppointments(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: 'Patient/FutureAppointments',
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient future appointments", errorRedirectPage, successRedirectPage);
  }

  async getPatientPastAndFutureAppointments(errorRedirectPage = null, successRedirectPage = null) {
    const pastAppts = await this.getPatientAllPastAppointments(errorRedirectPage, successRedirectPage);
    const futureAppts = await this.getPatientFutureAppointments(errorRedirectPage, successRedirectPage);

    const pastAndFutureAppts = pastAppts.concat(futureAppts);
    pastAndFutureAppts.sort((a, b) => new Date(a.slot) - new Date(b.slot));

    return pastAndFutureAppts;
  }

  async getPatientPCP(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Patient/Pcp`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving Patient PCP", errorRedirectPage, successRedirectPage);
  }

  async getPatientSchedulingTickets(errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Patient/SchedulingTickets`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient scheduling tickets", errorRedirectPage, successRedirectPage);
  }

  async getPatientConversations(lastMessageId = null, errorRedirectPage = null, successRedirectPage = null) {
    let relativePath = 'Patient/Conversations';

    if (lastMessageId) {
      relativePath = `${relativePath}?LastMessageId=${lastMessageId}`;
    }

    const options = {
      relativePath: relativePath,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient messages", errorRedirectPage, successRedirectPage);
  }

  async getPatientCoverages(epicDepartmentId, errorRedirectPage = null, successRedirectPage = null) {
    const options = {
      relativePath: `Patient/Coverages?epicDepartmentId=${epicDepartmentId}`,
    };

    return this.#luminisHttpClient.get(options, "Error retrieving patient coverages", errorRedirectPage, successRedirectPage);
  }

  /**
   * Logout of the API, you should redirect to the login page in every case EXCEPT when you are already on it.
   * @param onlyLH False, except when already on the login page.
   * @returns {Promise<*>}
   */
  async logout(onlyLH = false) {
    if (!onlyLH) {
      return this.#luminisHttpClient.redirect("Authentication/auth_logout");
    } else {
      const options = {
        relativePath: `Authentication/auth_logout_no_redirect`,
      };

      return this.#luminisHttpClient.get(options, "Error logging out patient");
    }
  }

  async getLoggedInUser() {
    const options = {
      relativePath: `Authentication/auth_check`,
    };

    // we are passing errors back to the mychart page to handle the error, as an
    // error when checking for a logged in user just means a logged out user.
    return this.#luminisHttpClient.get(options, null, null, null, true);
  }
}

